import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { FileInfo } from "../../models";
import { fileService as FileService } from "../../services/file.service";
import { Bank, IBanklet } from "../../helpers/hooks/useBank";
import { Endpoints } from "../../helpers/Endpoints";

export class UploadedFile implements IBanklet {
    public name: string;
    public status: string;
    public uniqueName: string;

    constructor(name: string, status: string) {
        this.name = name;
        this.status = status;
        // pad name so that its lench is 32 characters
        this.uniqueName = name;
        while (this.uniqueName.length < 32) {
            this.uniqueName += "a";
        }
        // randomize the unique name
        this.uniqueName = this.uniqueName.replace(/[a-z]/g, () => {
            return String.fromCharCode(Math.floor(Math.random() * 26) + 97);
        });
    }

    amI(other: IBanklet): boolean {
        return this.uniqueName === (other as UploadedFile).uniqueName;
    }
}

export default function Dropbox({
    className,
    label,
    url,
    selectedAttorney,
    bank,
}: {
    className: string;
    label: string;
    url: string;
    selectedAttorney?: string;
    bank: Bank<UploadedFile>;
}) {
    const filenameRegex = /[^a-zA-Z0-9_-]+/g;
    const f: FileInfo[] = [];
    const [files, setFiles] = useState(f);
    const [attorneyId, setAttorneyId] = useState(selectedAttorney);
    const [render, setRender] = useState<UploadedFile[]>();

    const get = (): UploadedFile[] => {
        const files = bank.get();
        console.log("files", files);
        return files;
    };
    const add = (file: UploadedFile) => bank.add(file);
    const put = (file: UploadedFile, callback: (file: UploadedFile) => void) =>
        bank.update(file, callback);

    const clear = () => {
        bank.destroy();
    };

    useEffect(() => {
        console.log("attorney: ", selectedAttorney);
        setAttorneyId(selectedAttorney);
    }, [selectedAttorney]);

    useEffect(() => {
        console.log("render: ", render);
    }, [render]);

    const onDrop = useCallback(
        (acceptedFiles) => {
            const filInfo = [...files];
            const start = performance.now();
            clear();
            const end = performance.now();
            console.log("add time: ", end - start);
            acceptedFiles.forEach((file: any) => {
                const uploadingFile = new UploadedFile(file.name, "Uploading");
                add(uploadingFile);
                const start = performance.now();
                if (
                    !file.name.endsWith(".pdf") &&
                    !file.name.endsWith(".zip") &&
                    !file.name.endsWith(".docx")
                ) {
                    return;
                }

                let sanatizedName = "";

                if (file.name.toLowerCase().includes(".zip")) {
                    sanatizedName =
                        file.name
                            .toLowerCase()
                            .split(".zip")[0]
                            .replace(filenameRegex, "") + ".zip";
                } else if (file.name.toLowerCase().includes(".docx")) {
                    sanatizedName =
                        file.name
                            .toLowerCase()
                            .split(".docx")[0]
                            .replace(filenameRegex, "") + ".docx";
                } else {
                    sanatizedName =
                        file.name
                            .toLowerCase()
                            .split(".pdf")[0]
                            .replace(filenameRegex, "") + ".pdf";
                }

                put(uploadingFile, (filePut: UploadedFile) => {
                    filePut.name = sanatizedName;
                    filePut.status = "Uploading";
                });
                setRender([...get()]);

                FileService.thirdParty({
                    fileServiceUrl: Endpoints.FileManager,
                    url: url,
                    file: file,
                    filename: file.name,
                    attorneyId: attorneyId ?? "",
                }).then(
                    (resp) => {
                        //setRefresh(!refresh);
                        filInfo.push({
                            filename: file.name,
                            size: 0, //file.size,
                        });
                        resp.text().then((_) => {
                            put(uploadingFile, (filePut: UploadedFile) => {
                                filePut.status = "Uploaded";
                            });
                            setRender([...get()]);
                        });
                    },
                    (error) => {
                        console.log(error.message);
                        put(uploadingFile, (filePut: UploadedFile) => {
                            filePut.status = "Failed";
                        });
                        setRender([...get()]);
                    }
                );
                const end = performance.now();
                console.log(
                    "Time to upload: " + (end - start) + " milliseconds"
                );
            });

            setFiles(filInfo);
        },
        [files, get, attorneyId]
    );

    const onFileUpload = useCallback(
        (event: React.FormEvent) => {
            event.preventDefault();

            const input = event.target as HTMLInputElement;

            if (!input.files?.length) {
                return;
            }
            clear();
            for (let i = 0; i < input.files.length; i++) {
                const f = input.files[i] as File;
                const uploadingFile = new UploadedFile(f.name, "Uploading");
                add(uploadingFile);

                if (
                    !f.name.toLowerCase().includes(".zip") &&
                    !f.name.toLowerCase().includes(".pdf") &&
                    !f.name.toLowerCase().endsWith(".docx")
                ) {
                    // TODO
                } else {
                    let sanatizedName = "";
                    const backName = f.name;

                    if (backName.toLowerCase().includes(".zip")) {
                        sanatizedName =
                            backName
                                .toLowerCase()
                                .split(".zip")[0]
                                .replace(filenameRegex, "") + ".zip";
                    } else if (backName.toLowerCase().includes(".docx")) {
                        sanatizedName =
                            backName
                                .toLowerCase()
                                .split(".docx")[0]
                                .replace(filenameRegex, "") + ".docx";
                    } else {
                        sanatizedName =
                            backName
                                .toLowerCase()
                                .split(".pdf")[0]
                                .replace(filenameRegex, "") + ".pdf";
                    }

                    put(uploadingFile, (filePut: UploadedFile) => {
                        filePut.name = sanatizedName;
                        filePut.status = "Uploading";
                    });
                    setRender([...get()]);
                    console.log({ Upload: { ...f } });

                    FileService.thirdParty({
                        fileServiceUrl: Endpoints.FileManager,
                        url: url,
                        file: f,
                        filename: sanatizedName,
                        attorneyId: attorneyId ?? "",
                    }).then(
                        (_resp) => {
                            put(uploadingFile, (filePut: UploadedFile) => {
                                filePut.status = "Uploaded";
                                console.log(filePut);
                            });
                            setRender([...get()]);
                        },
                        (error) => {
                            console.log("ERROR " + error);
                            put(uploadingFile, (filePut: UploadedFile) => {
                                filePut.status = "Failed";
                                console.log(filePut);
                            });
                            setRender([...get()]);
                        }
                    );
                }
            }
        },
        [attorneyId, get]
    );

    const { getRootProps, getInputProps } = useDropzone({
        onDrop: onDrop,
        noClick: true,
        noKeyboard: true,
        maxSize: 35000000,
        accept: "application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/zip, application/x-zip-compressed, multipart/x-zip",
        multiple: true,
    });
    return (
        <div className={className}>
            <div
                {...getRootProps({
                    className: "dropzone",
                })}
                style={{ border: "none" }}
            >
                <input {...getInputProps()} />
                <div className="text-center">
                    <i className="h1 text-muted ri-upload-cloud-2-line">...</i>
                    <h3 className="text-center">{label}</h3>
                </div>

                <div className="fileupload btn btn-success waves-effect waves-light mb-3">
                    <span>
                        <i className="mdi mdi-cloud-upload me-1"></i> Upload
                        Files
                    </span>
                    <input
                        type="file"
                        className="upload"
                        onChange={(event) => {
                            onFileUpload(event);
                            event.target.value = "";
                        }}
                        multiple
                    />
                </div>
                <div className="row">
                    <div
                        className="table-responsive"
                        style={{ overflow: "visible" }}
                    >
                        <table
                            className="table table-centered  table-nowrap mb-0"
                            style={{ zIndex: 0 }}
                        >
                            <thead className="table-light">
                                <tr>
                                    <th></th>
                                    <th scope="col">File Name</th>
                                    <th scope="col">File Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(render || []).map((file) => {
                                    return (
                                        <tr>
                                            <td>
                                                {file.status === "Uploading" ? (
                                                    <div
                                                        className="spinner-border text-primary m-2"
                                                        role="status"
                                                    />
                                                ) : (
                                                    <div></div>
                                                )}
                                            </td>
                                            <td>{file.name}</td>
                                            <td>{file.status}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}
